// Componente LayoutSignUp - Layout principal para el flujo de registro
// Reutiliza la lógica y estética de amarsi-app para la web

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AmarsiLogo from '../Icons/AmarsiLogo';
import Card from './Card';
import ThemeToggleButton from './ThemeToggleButton';
import Button from './Button';
import { FiChevronLeft } from 'react-icons/fi';

/**
 * Componente LayoutSignUp
 * 
 * Layout principal con barra de progreso para el flujo de registro
 * 
 * @param {Object} props - Propiedades del componente
 * @param {number} props.step - Paso actual del registro (1-8)
 * @param {React.ReactNode} props.children - Contenido del layout
 * @param {boolean} props.showBackButton - Si mostrar botón de retroceso
 * @param {Function} props.onBackPress - Función del botón de retroceso
 * @returns {JSX.Element} Layout de registro
 */
const LayoutSignUp = ({ 
  step = 1, 
  children, 
  showBackButton = false, 
  withProgressBar = true
}) => {
  // Hook de navegación para React Router
  const navigation = useNavigate();

  // Calcular progreso
  const progress = (step / 8) * 100;


  return (
    <div className="min-h-screen flex flex-col justify-between bg-neutral-50 dark:bg-neutral-900">

      {/* Botón de retroceso (opcional) */}
      <div className="w-full max-w-lg mx-auto pt-8 px-4 flex justify-between items-center">
        <Button
          onClick={() => navigation('/')}
          variant="ghost-secondary"
          className='pr-4'
        >
          <FiChevronLeft className='w-5 h-5' />
          <span>Volver</span>
        </Button>

        {/* Botón de modo oscuro */}
        <ThemeToggleButton />
      </div>


      {/* Contenido principal */}
      <main className={`container mx-auto px-4 ${!withProgressBar ? 'pt-12' : ''}`}>


        {/* Barra de progreso - solo si withProgressBar es true */}
        {withProgressBar && (
          <div className="container mx-auto px-4 my-4">
            <div className="max-w-lg mx-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Paso {step} de 8
                </span>
                <div></div>
              </div>
              
              {/* Barra de progreso visual */}
              <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-primary-900 to-primary-600 h-full rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        )}

        <div className='max-w-lg mx-auto'>

          {/* Contenido dinámico */}
          <Card className='p-0 overflow-hidden'>
            {children}
          </Card>
        </div>
      </main>


      {/* Footer */}
      <footer className="bg-white dark:bg-neutral-800 mt-20 border-t border-gray-200 dark:border-neutral-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0 gap-4">
              <AmarsiLogo width='2rem' height='2rem'/>
              <span className="text-gray-900 dark:text-white font-semibold">Amarsi</span>
              <span className="text-gray-500">© {new Date().getFullYear()} Todos los derechos reservados.</span>

            </div>
            <div className="flex space-x-6 text-sm">
              <Link 
                to="/politicas-easi" 
                className="text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors"
              >
                Políticas EASI
              </Link>
              <Link 
                to="/privacidad" 
                className="text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors"
              >
                Privacidad
              </Link>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LayoutSignUp;
